import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Camera, CheckCircle2, FileText, Loader2, Lock, RefreshCw, Upload, AlertTriangle, Clock, ShieldCheck } from 'lucide-react';
import SiteLogo from '../../components/Layout/SiteLogo';
import DocumentCamera from '../../components/DocumentChecklist/DocumentCamera';
import { canUseLiveCamera } from '../../components/DocumentChecklist/documentQuality';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import {
  ChecklistFile,
  ChecklistItem,
  ChecklistProgress,
  ChecklistStatus,
  Side,
  VisaType,
  apiFileUrl,
  errorMessage,
  itemText,
  prepareUpload,
  sidesOf,
} from '../../components/DocumentChecklist/documentChecklist';
import { Button, IconButton } from '../../components/UI';
import { useConfirm } from '../../contexts/ConfirmContext';

interface PublicChecklist {
  valid: boolean;
  visaType: VisaType;
  lang: string;
  status: ChecklistStatus;
  expiresAt: string;
  submittedAt?: string | null;
  clientFirstName: string;
  progress: ChecklistProgress;
  items: ChecklistItem[];
  consentRequired: boolean;
  consent: ConsentTerms;
  consentAcceptedAt: string | null;
  retentionDays: number;
  filesPurgeAt: string | null;
  filesPurged: boolean;
}

interface ConsentTerms {
  version: string;
  title: string;
  paragraphs: string[];
  accept: string;
}

function dateLocale(lang: string) {
  return lang === 'en' ? 'en-GB' : lang === 'es' ? 'es-ES' : 'pt-BR';
}

const DocumentChecklistFill: React.FC = () => {
  const confirm = useConfirm();
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PublicChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const base = `/documentos-cliente/public/c/${token}`;

  const load = useCallback(
    async (applyLang: boolean) => {
      try {
        const res = await axios.get<PublicChecklist>(base);
        setData(res.data);
        if (applyLang && res.data.lang && !localStorage.getItem('neia_lang')) {
          i18n.changeLanguage(res.data.lang);
        }
      } catch (err: unknown) {
        const reason = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
        setFatal(
          reason === 'expired'
            ? t('documentChecklist.linkExpired')
            : reason === 'revoked'
              ? t('documentChecklist.linkRevoked')
              : t('documentChecklist.linkInvalid')
        );
      } finally {
        setLoading(false);
      }
    },
    [base, i18n, t]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const replaceItem = (item: ChecklistItem, progress: ChecklistProgress) => {
    setData((prev) =>
      prev ? { ...prev, progress, items: prev.items.map((i) => (i.key === item.key ? item : i)) } : prev
    );
  };

  const setItemError = (key: string, msg: string) => setItemErrors((prev) => ({ ...prev, [key]: msg }));

  const handleCheck = async (item: ChecklistItem, checked: boolean) => {
    replaceItem({ ...item, clientChecked: checked }, data!.progress);
    try {
      const res = await axios.post(`${base}/items/${item.key}/check`, { checked });
      setData((prev) => (prev ? { ...prev, progress: res.data.progress } : prev));
    } catch (err) {
      replaceItem(item, data!.progress);
      setItemError(item.key, errorMessage(err, t('documentChecklist.errGeneric')));
    }
  };

  const handleUpload = async (item: ChecklistItem, side: Side, picked: File) => {
    const busyKey = `${item.key}:${side}`;
    setBusy((prev) => ({ ...prev, [busyKey]: true }));
    setItemError(item.key, '');
    try {
      const file = await prepareUpload(picked);
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${base}/items/${item.key}/files/${side}`, fd);
      replaceItem(res.data.item, res.data.progress);
    } catch (err) {
      setItemError(item.key, errorMessage(err, t('documentChecklist.errUpload')));
    } finally {
      setBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleRemove = async (item: ChecklistItem, file: ChecklistFile) => {
    const busyKey = `${item.key}:${file.side}`;
    setBusy((prev) => ({ ...prev, [busyKey]: true }));
    try {
      const res = await axios.delete(`${base}/items/${item.key}/files/${file._id}`);
      replaceItem(res.data.item, res.data.progress);
    } catch (err) {
      setItemError(item.key, errorMessage(err, t('documentChecklist.errGeneric')));
    } finally {
      setBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const acceptConsent = async () => {
    setConsentSaving(true);
    setConsentError('');
    try {
      const res = await axios.post<PublicChecklist>(`${base}/consent`, {
        accepted: true,
        version: data?.consent.version,
      });
      setData(res.data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      // A equipe publicou uma versão nova enquanto o cliente lia: recarrega o texto e pede novo aceite
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        setConsentChecked(false);
        await load(false);
      }
      setConsentError(errorMessage(err, t('documentChecklist.errGeneric')));
    } finally {
      setConsentSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!data) return;
    if (
      data.progress.missingRequired > 0 &&
      !(await confirm({ message: t('documentChecklist.missingConfirm', { count: data.progress.missingRequired }), variant: 'warning' }))
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post<PublicChecklist>(`${base}/submit`);
      setData(res.data);
      setJustSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setFatal(errorMessage(err, t('documentChecklist.errGeneric')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (fatal || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full text-center space-y-4">
          <SiteLogo size="lg" className="justify-center" />
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <p className="text-gray-700">{fatal || t('documentChecklist.linkInvalid')}</p>
        </div>
      </div>
    );
  }

  const locked = data.status === 'completed';
  const lang = i18n.language;
  const { progress } = data;
  const pct = progress.total ? Math.round((progress.uploaded / progress.total) * 100) : 0;
  const expires = new Date(data.expiresAt).toLocaleDateString(dateLocale(lang));

  if (data.consentRequired) {
    return (
      <div className="min-h-screen bg-gray-50 pb-10">
        <div className="max-w-2xl mx-auto px-4 pt-6 sm:pt-10">
          <header className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" className="justify-center" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {data.clientFirstName
                ? t('documentChecklist.greeting', { name: data.clientFirstName })
                : t('documentChecklist.title')}
            </h1>
            <div className="flex justify-center mt-4">
              <LanguageSwitcher compact variant="floating" className="shadow-md" />
            </div>
          </header>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
              <ShieldCheck className="w-5 h-5 text-primary-600 flex-shrink-0" />
              {data.consent.title}
            </h2>
            <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
              {data.consent.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <p className="mt-3 text-sm">
              <Link to="/privacidade" target="_blank" className="text-primary-700 underline hover:text-primary-800">
                {t('documentChecklist.consentPrivacyLink')}
              </Link>
            </p>

            <label className="mt-5 flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800">
              <input
                type="checkbox"
                className="check-input mt-0.5"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>{data.consent.accept}</span>
            </label>
            {consentError && <p className="mt-2 text-sm text-red-600">{consentError}</p>}
            <Button className="w-full mt-4"
              type="button"
              onClick={acceptConsent}
              disabled={!consentChecked || consentSaving}>
              {consentSaving ? t('documentChecklist.consentSaving') : t('documentChecklist.consentContinue')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <div className="max-w-2xl mx-auto px-4 pt-6 sm:pt-10">
        <header className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" className="justify-center" />
          </div>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-primary-700 mb-1">
            {t('documentChecklist.visaLabel')}: {t(`visaForm.goals.${data.visaType}`)}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {data.clientFirstName
              ? t('documentChecklist.greeting', { name: data.clientFirstName })
              : t('documentChecklist.title')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base mt-2">{t('documentChecklist.intro')}</p>
          <div className="flex justify-center mt-4">
            <LanguageSwitcher compact variant="floating" className="shadow-md" />
          </div>
        </header>

        {(justSubmitted || data.status === 'submitted') && !locked && (
          <div className="mb-4 flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <p>{t('documentChecklist.submitted')}</p>
          </div>
        )}
        {locked && (
          <div className="mb-4 flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <p>{t('documentChecklist.completed')}</p>
          </div>
        )}

        <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-gray-50/95 backdrop-blur">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="font-medium text-gray-800">
              {t('documentChecklist.progress', { done: progress.uploaded, total: progress.total })}
            </span>
            <span className="text-gray-500 tabular-nums">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full bg-primary-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <p className="text-xs text-gray-500 my-3">{t('documentChecklist.photoTips')}</p>

        <ul className="space-y-4">
          {data.items.map((item) => (
            <ItemCard
              key={item.key}
              item={item}
              lang={lang}
              locked={locked}
              busy={busy}
              error={itemErrors[item.key]}
              onCheck={handleCheck}
              onUpload={handleUpload}
              onRemove={handleRemove}
            />
          ))}
        </ul>

        <div className="mt-6 text-center text-xs text-gray-600 space-y-1.5">
          <p className="flex items-start justify-center gap-1.5">
            <Lock className="w-3.5 h-3.5 mt-px flex-shrink-0" />
            <span>
              {data.filesPurged
                ? t('documentChecklist.filesPurged')
                : data.filesPurgeAt
                  ? t('documentChecklist.retentionScheduled', {
                      date: new Date(data.filesPurgeAt).toLocaleDateString(dateLocale(lang)),
                    })
                  : t('documentChecklist.retentionNotice', { count: data.retentionDays })}
            </span>
          </p>
          {data.consentAcceptedAt && (
            <p>
              {t('documentChecklist.consentAccepted', {
                date: new Date(data.consentAcceptedAt).toLocaleDateString(dateLocale(lang)),
              })}{' '}
              <button type="button" onClick={() => setShowTerms((v) => !v)} className="underline text-primary-700">
                {t('documentChecklist.viewConsent')}
              </button>
            </p>
          )}
          {showTerms && (
            <div className="text-left bg-white border border-gray-200 rounded-lg p-4 space-y-2 text-gray-700">
              <p className="font-semibold text-gray-900">{data.consent.title}</p>
              {data.consent.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          )}
          {!locked && <p className="text-gray-500">{t('documentChecklist.validUntil', { date: expires })}</p>}
        </div>
      </div>

      {!locked && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <div className="max-w-2xl mx-auto">
            <Button className="w-full"
              type="button"
              onClick={handleSubmit}
              disabled={submitting || Object.values(busy).some(Boolean)}>
              {submitting
                ? t('documentChecklist.submitting')
                : data.status === 'submitted'
                  ? t('documentChecklist.submitAgain')
                  : t('documentChecklist.submit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface ItemCardProps {
  item: ChecklistItem;
  lang: string;
  locked: boolean;
  busy: Record<string, boolean>;
  error?: string;
  onCheck: (item: ChecklistItem, checked: boolean) => void;
  onUpload: (item: ChecklistItem, side: Side, file: File) => void;
  onRemove: (item: ChecklistItem, file: ChecklistFile) => void;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, lang, locked, busy, error, onCheck, onUpload, onRemove }) => {
  const { t } = useTranslation();
  const text = itemText(item, lang);
  const approved = item.review.status === 'approved';
  const rejected = item.review.status === 'rejected';
  const readOnly = locked || approved;
  const complete = sidesOf(item).every((s) => item.files.some((f) => f.side === s));

  return (
    <li
      className={`bg-white rounded-xl shadow-sm border p-4 ${
        rejected ? 'border-red-300' : approved ? 'border-green-300' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-900 leading-snug">{text.label}</h2>
          {text.description && <p className="text-sm text-gray-600 mt-0.5">{text.description}</p>}
        </div>
        <StatusBadge item={item} complete={complete} />
      </div>

      {rejected && item.review.note && (
        <p className="mt-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
          {t('documentChecklist.rejectedNote', { note: item.review.note })}
        </p>
      )}

      {!readOnly && (
        <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 select-none">
          <input
            type="checkbox"
            className="check-input"
            checked={item.clientChecked}
            onChange={(e) => onCheck(item, e.target.checked)}
          />
          {t('documentChecklist.haveIt')}
        </label>
      )}

      <div className={`mt-3 grid gap-3 ${item.sides === 'front_back' ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
        {sidesOf(item).map((side) => (
          <UploadSlot
            key={side}
            item={item}
            side={side}
            file={item.files.find((f) => f.side === side)}
            busy={!!busy[`${item.key}:${side}`]}
            readOnly={readOnly}
            onUpload={onUpload}
            onRemove={onRemove}
          />
        ))}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </li>
  );
};

const StatusBadge: React.FC<{ item: ChecklistItem; complete: boolean }> = ({ item, complete }) => {
  const { t } = useTranslation();
  if (item.review.status === 'approved') {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-800">
        <CheckCircle2 className="w-3.5 h-3.5" />
        {t('documentChecklist.approved')}
      </span>
    );
  }
  if (item.review.status === 'rejected') {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-800">
        <AlertTriangle className="w-3.5 h-3.5" />
        {t('documentChecklist.rejected')}
      </span>
    );
  }
  if (complete) {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700">
        <Clock className="w-3.5 h-3.5" />
        {t('documentChecklist.inReview')}
      </span>
    );
  }
  return (
    <span
      className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded-full ${
        item.required ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {item.required ? t('documentChecklist.required') : t('documentChecklist.optional')}
    </span>
  );
};

interface UploadSlotProps {
  item: ChecklistItem;
  side: Side;
  file?: ChecklistFile;
  busy: boolean;
  readOnly: boolean;
  onUpload: (item: ChecklistItem, side: Side, file: File) => void;
  onRemove: (item: ChecklistItem, file: ChecklistFile) => void;
}

const UploadSlot: React.FC<UploadSlotProps> = ({ item, side, file, busy, readOnly, onUpload, onRemove }) => {
  const { t, i18n } = useTranslation();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [reviewFile, setReviewFile] = useState<File | null>(null);
  const [liveCameraFailed, setLiveCameraFailed] = useState(false);
  const accept = item.acceptPdf ? 'image/jpeg,image/png,image/webp,application/pdf' : 'image/jpeg,image/png,image/webp';
  const cameraTitle = `${itemText(item, i18n.language).label} — ${t(`documentChecklist.side.${side}`)}`;

  // Câmera do app (moldura + dicas de qualidade); sem suporte ou sem permissão, cai na câmera nativa
  const openCamera = () => {
    if (canUseLiveCamera() && !liveCameraFailed) setCameraOpen(true);
    else cameraRef.current?.click();
  };

  const handleCameraFallback = useCallback(() => {
    setCameraOpen(false);
    setLiveCameraFailed(true);
  }, []);

  // Fotos (da galeria ou da câmera nativa) passam pela revisão de qualidade; PDF vai direto
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (picked.type.startsWith('image/')) setReviewFile(picked);
    else onUpload(item, side, picked);
  };

  const finish = (confirmed: File) => {
    setCameraOpen(false);
    setReviewFile(null);
    onUpload(item, side, confirmed);
  };

  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-2 flex flex-col">
      <span className="text-xs font-medium text-gray-500 mb-1.5">{t(`documentChecklist.side.${side}`)}</span>

      {busy ? (
        <div className="aspect-[4/3] flex flex-col items-center justify-center text-sm text-gray-500 gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          {t('documentChecklist.uploading')}
        </div>
      ) : file ? (
        <>
          <a
            href={apiFileUrl(file.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="block aspect-[4/3] rounded-md overflow-hidden bg-white border border-gray-200"
          >
            {file.mime === 'application/pdf' ? (
              <span className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-1">
                <FileText className="w-8 h-8" />
                <span className="text-xs">{t('documentChecklist.pdf')}</span>
              </span>
            ) : (
              <img src={apiFileUrl(file.url)} alt="" className="w-full h-full object-cover" />
            )}
          </a>
          {!readOnly && (
            <div className="flex gap-2 mt-2">
              <Button variant="ghost" size="sm" className="flex-1"
                type="button"
                onClick={openCamera}>
                <RefreshCw className="w-3.5 h-3.5" />
                {t('documentChecklist.replace')}
              </Button>
              <IconButton action="delete" label={t('documentChecklist.remove')} onClick={() => onRemove(item, file)} className="inline-flex" iconClassName="w-3.5 h-3.5" />
            </div>
          )}
        </>
      ) : readOnly ? (
        <div className="aspect-[4/3]" />
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={openCamera}>
            <Camera className="w-4 h-4" />
            {t('documentChecklist.takePhoto')}
          </Button>
          <Button variant="ghost" size="sm"
            type="button"
            onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4" />
            {t('documentChecklist.chooseFile')}
          </Button>
        </div>
      )}

      {liveCameraFailed && !file && !readOnly && (
        <p className="mt-1.5 text-[11px] leading-snug text-gray-600">{t('documentChecklist.camera.unavailable')}</p>
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={onPick} />

      {(cameraOpen || reviewFile) && (
        <DocumentCamera
          title={cameraTitle}
          frame={item.sides === 'front_back' ? 'card' : 'page'}
          file={reviewFile}
          onConfirm={finish}
          onClose={() => {
            setCameraOpen(false);
            setReviewFile(null);
          }}
          onFallback={handleCameraFallback}
        />
      )}
    </div>
  );
};

export default DocumentChecklistFill;
